import { useEffect, useRef, useState } from 'react';
import { fetchCampaignItem } from 'src/app/modules/PruleadInsightsReporting/network/campaignCrud';
import { CampaignItem } from 'src/app/modules/PruleadInsightsReporting/types/insights-types';

interface HookProps {
  onSelectItem: (campaign: CampaignItem | null) => void;
  onChange: (campaign: CampaignItem) => void;
  selectedId?: string;
}

export const useCampaignSelect = ({ onSelectItem, selectedId, onChange }: HookProps) => {
  const [selectedRowsState, setSelectedRowsState] = useState<CampaignItem | null>(null);
  const [isLoading, setIsLoading] = useState(!!selectedId);
  const [loadError, setLoadError] = useState(false);
  const [popUpVisible, setPopUpVisible] = useState<boolean>(false);
  const previousSelection = useRef<CampaignItem | null>(null);
  const confirmedSelection = useRef<CampaignItem | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const onCancel = () => {
    setSelectedRowsState(previousSelection.current);
    setPopUpVisible(false);
  };

  const onSelect = () => {
    previousSelection.current = selectedRowsState;
    setPopUpVisible(true);
  };

  const onSubmit = () => {
    confirmedSelection.current = selectedRowsState;
    onSelectItem(selectedRowsState);
    setPopUpVisible(false);
  };

  useEffect(() => {
    let active = true;
    setLoadError(false);
    if (selectedId && confirmedSelection.current?._id === selectedId) {
      setSelectedRowsState(confirmedSelection.current);
      setIsLoading(false);
      return;
    }
    confirmedSelection.current = null;
    setSelectedRowsState(null);
    previousSelection.current = null;
    setIsLoading(!!selectedId);
    if (selectedId) {
      const fetchCampaign = async () => {
        try {
          const result = await fetchCampaignItem(selectedId);
          if (!active) return;
          confirmedSelection.current = result;
          setSelectedRowsState(result);
          onChangeRef.current(result);
        } catch {
          if (active) setLoadError(true);
        } finally {
          if (active) setIsLoading(false);
        }
      };
      void fetchCampaign();
    }
    return () => {
      active = false;
    };
  }, [selectedId]);

  return {
    popUpVisible,
    isLoading,
    loadError,
    selectedRowsState,
    setSelectedRowsState,
    onCancel,
    onSelect,
    onSubmit,
  };
};
